/** "Get started" checklist for a new team's administrators (on My View until hidden). */
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useMeta } from '../meta';
import { ErrorBox } from '../components';
import { t } from '../i18n';

interface Onboarding {
  show: boolean;
  steps?: { project: boolean; issue: boolean; invite: boolean; connect: boolean };
}

export function GettingStarted() {
  const { meta } = useMeta();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data } = useQuery({ queryKey: ['onboarding', meta.team?.id], queryFn: () => api.get<Onboarding>('/api/onboarding') });
  const dismiss = useMutation({
    mutationFn: () => api.post('/api/onboarding/dismiss'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['onboarding'] }),
  });
  const sample = useMutation({
    mutationFn: () => api.post<{ project_id: number }>('/api/onboarding/sample'),
    onSuccess: async () => {
      await qc.invalidateQueries();
      navigate('/issues');
    },
  });
  if (!data?.show || !data.steps) return null;
  const s = data.steps;
  const steps: { done: boolean; title: string; text: string; to: string; action: string }[] = [
    { done: s.project, title: t('Create a project'), text: t('Projects hold the issues, with their own categories and versions.'), to: '/manage/projects', action: t('Create Project') },
    { done: s.issue, title: t('Report the first issue'), text: t('A bug, a task or an idea: anything the team should work on.'), to: '/report', action: t('Report Issue') },
    { done: s.invite, title: t('Invite your team'), text: t('Colleagues get an e-mail with a link to join.'), to: '/manage/team', action: t('Invite people') },
    {
      done: s.connect,
      title: t('Bring your data and tools'),
      text: t('Import a Mantis database, or connect other tools with an API key or webhooks.'),
      to: '/manage/import',
      action: t('Import from Mantis'),
    },
  ];
  const done = steps.filter((x) => x.done).length;
  return (
    <section className="card getting-started">
      <div className="card-head">
        <h2>{t('Get started with {team}', { team: meta.team?.name ?? '' })}</h2>
        <div className="card-actions">
          <span className="muted small">{t('{done} of {total} done', { done, total: steps.length })}</span>
          <button className="link" onClick={() => dismiss.mutate()}>
            {t('Hide')}
          </button>
        </div>
      </div>
      <div className="card-body">
        <ol className="gs-steps">
          {steps.map((x) => (
            <li key={x.title} className={x.done ? 'done' : ''}>
              <span className="gs-check" aria-hidden>
                {x.done ? '✓' : ''}
              </span>
              <div>
                <strong>{x.title}</strong>
                <div className="muted small">{x.text}</div>
              </div>
              {!x.done && (
                <Link className="btn" to={x.to}>
                  {x.action}
                </Link>
              )}
            </li>
          ))}
        </ol>
        {!s.project && (
          <p className="muted small">
            {t('Just looking around?')}{' '}
            <button className="link" disabled={sample.isPending} onClick={() => sample.mutate()}>
              {t('Create a sample project with example issues')}
            </button>{' '}
            {t('(you can delete it later)')}
          </p>
        )}
        <ErrorBox error={dismiss.error || sample.error} />
      </div>
    </section>
  );
}
